import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';

@Injectable({
  providedIn: 'root',
})
export class SiaWakeLockService {
  private wakeLock: WakeLockSentinel | null = null;
  private isActive = false;
  private retryTimeoutId: any = null;
  private wakeLockReleaseHandler = () => this.handleWakeLockRelease();

  constructor() {
    // Handle visibility change to re-acquire wake lock when page becomes visible
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', () => {
        if (this.isActive && document.visibilityState === 'visible') {
          this.acquireWakeLock();
        }
      });
    }
  }

  /**
   * Activates the wake lock to prevent the screen from sleeping
   * @returns Promise<boolean> - true if successfully activated, false otherwise
   */
  async activate(): Promise<boolean> {
    if (this.isActive) {
      return true;
    }

    this.isActive = true;

    if (Capacitor.isNativePlatform()) {
      return this.activateNative();
    } else {
      return this.activateWeb();
    }
  }

  /**
   * Deactivates the wake lock, allowing the screen to sleep normally
   * @returns Promise<boolean> - true if successfully deactivated, false otherwise
   */
  async deactivate(): Promise<boolean> {
    if (!this.isActive) {
      return true;
    }

    this.isActive = false;

    // Clear retry timeout
    if (this.retryTimeoutId) {
      clearTimeout(this.retryTimeoutId);
      this.retryTimeoutId = null;
    }

    if (Capacitor.isNativePlatform()) {
      return this.deactivateNative();
    } else {
      return this.deactivateWeb();
    }
  }

  /**
   * Check if wake lock is currently active
   * @returns boolean
   */
  isWakeLockActive(): boolean {
    return this.isActive && (this.wakeLock ? !this.wakeLock.released : true);
  }

  /**
   * Activate wake lock for web browsers using Screen Wake Lock API
   */
  private async activateWeb(): Promise<boolean> {
    try {
      if ('wakeLock' in navigator && (navigator as any).wakeLock) {
        await this.acquireWakeLock();
        return true;
      } else {
        console.warn('Screen Wake Lock API not supported in this browser');
        // Fallback: use a hidden video element or audio element to prevent sleep
        return this.activateWebFallback();
      }
    } catch (error) {
      console.error('Failed to activate wake lock:', error);
      return this.activateWebFallback();
    }
  }

  /**
   * Acquire the actual wake lock using the Screen Wake Lock API
   */
  private async acquireWakeLock(): Promise<void> {
    try {
      const nav = navigator as any;
      if (nav.wakeLock && (!this.wakeLock || this.wakeLock.released)) {
        this.wakeLock = await nav.wakeLock.request('screen');
        this.wakeLock?.addEventListener('release', this.wakeLockReleaseHandler);
        console.log('Screen wake lock acquired');
      }
    } catch (error) {
      console.error('Failed to acquire wake lock:', error);
      throw error;
    }
  }

  /**
   * Handle wake lock release (e.g., when page becomes hidden)
   */
  private handleWakeLockRelease(): void {
    console.log('Screen wake lock was released');

    // If we should still be active, try to re-acquire after a delay
    if (this.isActive && document.visibilityState === 'visible') {
      this.retryTimeoutId = setTimeout(() => {
        this.acquireWakeLock().catch((error) => {
          console.error('Failed to re-acquire wake lock:', error);
        });
      }, 1000);
    }
  }

  /**
   * Fallback method for browsers that don't support Screen Wake Lock API
   */
  private activateWebFallback(): Promise<boolean> {
    return new Promise((resolve) => {
      try {
        // Create a hidden video element that plays silently to prevent sleep
        const video = document.createElement('video');
        video.id = 'sia-wake-lock-video';
        video.style.position = 'fixed';
        video.style.top = '-1000px';
        video.style.left = '-1000px';
        video.style.width = '1px';
        video.style.height = '1px';
        video.style.opacity = '0';
        video.style.pointerEvents = 'none';
        video.muted = true;
        video.loop = true;
        video.autoplay = true;
        video.playsInline = true;

        // Create a simple video data URL (1x1 transparent pixel)
        video.src =
          'data:video/mp4;base64,AAAAIGZ0eXBpc29tAAACAGlzb21pc28yYXZjMWF2YzAAAAABbW9vdgAAAGxtdmhkAAAAAAAAAAAAAAAAAAAD6AAABdwAAQAAAQAAAAAAAAAAAAAAAAEAAAAAAAAAAAAAAAAAAAABAAAAAAAAAAAAAAAAAABAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAgAAABhpb2RzAAAAABCAgIAQAE////9//w6AgIAEAAAAAYYdHJhawAAAFx0a2hkAAAAAwAAAAAAAAAAAAAAAQAAAAAAAAXcAAAAAAAAAAAAAAAAAAAAAAABAAAAAAAAAAAAAAAAAAAAAQAAAAAAAAAAAAAAAAAAQAAAAAAAAAAAAAAAAAA';

        document.body.appendChild(video);

        video.addEventListener('loadeddata', () => {
          video
            .play()
            .then(() => {
              console.log('Wake lock fallback activated');
              resolve(true);
            })
            .catch(() => {
              console.warn('Wake lock fallback failed');
              resolve(false);
            });
        });

        video.addEventListener('error', () => {
          console.warn('Wake lock fallback failed to load');
          resolve(false);
        });
      } catch (error) {
        console.error('Wake lock fallback error:', error);
        resolve(false);
      }
    });
  }

  /**
   * Deactivate wake lock for web browsers
   */
  private async deactivateWeb(): Promise<boolean> {
    try {
      // Release Screen Wake Lock API
      if (this.wakeLock && !this.wakeLock.released) {
        this.wakeLock?.removeEventListener(
          'release',
          this.wakeLockReleaseHandler,
        );
        await this.wakeLock.release();
        this.wakeLock = null;
        console.log('Screen wake lock released');
      }

      // Remove fallback video element
      const video = document.getElementById('sia-wake-lock-video');
      if (video) {
        video.remove();
        console.log('Wake lock fallback deactivated');
      }

      return true;
    } catch (error) {
      console.error('Failed to deactivate wake lock:', error);
      return false;
    }
  }

  /**
   * Activate wake lock for native platforms (iOS/Android) using Capacitor
   */
  private async activateNative(): Promise<boolean> {
    try {
      // For Capacitor apps, we can use the @capacitor/device plugin
      // or implement a custom plugin. For now, we'll use a web-based approach
      // that should work in most mobile web views

      // Check if we can use the Screen Wake Lock API in the mobile webview
      if ('wakeLock' in navigator && (navigator as any).wakeLock) {
        await this.acquireWakeLock();
        return true;
      }

      // Fallback for mobile: prevent sleep using audio context and timer
      return this.activateNativeFallback();
    } catch (error) {
      console.error('Failed to activate native wake lock:', error);
      return this.activateNativeFallback();
    }
  }

  /**
   * Fallback method for native platforms
   */
  private activateNativeFallback(): Promise<boolean> {
    return new Promise((resolve) => {
      try {
        // Use a combination of audio context and timer to prevent sleep
        const audioContext = new (window.AudioContext ||
          (window as any).webkitAudioContext)();
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();

        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);

        // Set extremely low volume (essentially silent)
        gainNode.gain.setValueAtTime(0.0001, audioContext.currentTime);
        oscillator.frequency.setValueAtTime(20000, audioContext.currentTime); // Above human hearing range

        oscillator.start();

        // Store reference for cleanup
        (window as any).siaWakeLockAudioContext = audioContext;
        (window as any).siaWakeLockOscillator = oscillator;

        console.log('Native wake lock fallback activated');
        resolve(true);
      } catch (error) {
        console.error('Native wake lock fallback error:', error);
        resolve(false);
      }
    });
  }

  /**
   * Deactivate wake lock for native platforms
   */
  private async deactivateNative(): Promise<boolean> {
    try {
      // Release Screen Wake Lock API if available
      if (this.wakeLock && !this.wakeLock.released) {
        this.wakeLock?.removeEventListener(
          'release',
          this.wakeLockReleaseHandler,
        );
        await this.wakeLock.release();
        this.wakeLock = null;
        console.log('Native screen wake lock released');
      }

      // Clean up audio context fallback
      if ((window as any).siaWakeLockOscillator) {
        (window as any).siaWakeLockOscillator.stop();
        (window as any).siaWakeLockOscillator = null;
      }

      if ((window as any).siaWakeLockAudioContext) {
        await (window as any).siaWakeLockAudioContext.close();
        (window as any).siaWakeLockAudioContext = null;
      }

      console.log('Native wake lock fallback deactivated');
      return true;
    } catch (error) {
      console.error('Failed to deactivate native wake lock:', error);
      return false;
    }
  }
}
